import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { embedDashboard } from "@superset-ui/embedded-sdk";

const supersetUrl = "https://atacama.dev.superset.toughblue.com";
const supersetApiUrl = `${supersetUrl}/api/v1/security`;

let dashboardSlugCache = null;

async function loginAsAdmin() {
  const loginBody = {
    username: "admin", // secure these in backend or .env
    password: "admin",
    provider: "db",
    refresh: true,
  };

  const { data } = await axios.post(
    `${supersetApiUrl}/login`,
    loginBody,
    { headers: { "Content-Type": "application/json" } }
  );

  return data.access_token;
}

async function fetchDashboardsBySlug() {
  if (dashboardSlugCache) return dashboardSlugCache;

  const accessToken = await loginAsAdmin();

  const { data: dashboardsRes } = await axios.get(
    `${supersetUrl}/api/v1/dashboard/`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  const dashboardsList = dashboardsRes.result || dashboardsRes;
  if (!dashboardsList || dashboardsList.length === 0) return {};

  const requests = dashboardsList.map((dash) =>
    axios
      .get(`${supersetUrl}/api/v1/dashboard/${dash.slug}/embedded`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => ({
        slug: dash.slug,
        uuid: res.data?.result?.uuid || null,
      }))
      .catch(() => ({ slug: dash.slug, uuid: null }))
  );

  const results = await Promise.all(requests);

  dashboardSlugCache = results.reduce((acc, { slug, uuid }) => {
    if (uuid) acc[slug] = uuid;
    return acc;
  }, {});

  return dashboardSlugCache;
}

async function getTokenAndEmbed(slug) {
  const dashboardsBySlug = await fetchDashboardsBySlug();

  if (!dashboardsBySlug || Object.keys(dashboardsBySlug).length === 0) {
    renderNoDashboardMessage("No dashboards available.");
    return;
  }

  const dashboardId = dashboardsBySlug[slug];
  if (!dashboardId) {
    renderNoDashboardMessage(`No dashboard found for role "${slug}".`);
    return;
  }

  const accessToken = await loginAsAdmin();
  const guestTokenBody = {
    resources: [{ type: "dashboard", id: dashboardId }],
    rls: [],
    user: { username: "report-viewer", first_name: "Report", last_name: "Viewer" },
  };

  const { data: guestData } = await axios.post(
    `${supersetApiUrl}/guest_token/`,
    guestTokenBody,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  await embedDashboard({
    id: dashboardId,
    supersetDomain: supersetUrl,
    mountPoint: document.getElementById("superset-container"),
    fetchGuestToken: () => guestData.token,
    dashboardUiConfig: { hideTitle: true },
  });

  resizeIframe(); // Initial sizing
}

function resizeIframe() {
  const iframe = document.querySelector("iframe");
  if (iframe) {
    iframe.style.width = "100%";
    iframe.style.height = `${window.innerHeight}px`; // Full browser window height
    iframe.style.border = "none";
  }
}

function renderNoDashboardMessage(message) {
  const container = document.getElementById("superset-container");
  if (container) {
    container.innerHTML = `
      <div style="display:flex;justify-content:center;align-items:center;height:100vh;background:#f9fafb;">
        <div style="background:white;padding:2rem;border-radius:12px;box-shadow:0 4px 15px rgba(0,0,0,0.08);text-align:center;">
          <div style="font-size:3rem;color:#ff6b6b;">⚠️</div>
          <h2 style="color:#111;margin-bottom:0.5rem;">Dashboard Not Available</h2>
          <p style="color:#555;">${message}</p>
        </div>
      </div>
    `;
  }
}

function App() {
  const [slug, setSlug] = useState("");

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => resizeIframe();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLoadDashboard = () => {
    if (slug.trim()) {
      getTokenAndEmbed(slug.trim());
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <input
        type="text"
        placeholder="Enter dashboard role"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        style={{
          padding: "0.5rem",
          marginRight: "0.5rem",
          width: "250px",
          border: "1px solid #ccc",
          borderRadius: "6px",
        }}
      />
      <button
        onClick={handleLoadDashboard}
        style={{
          padding: "0.5rem 1rem",
          backgroundColor: "#2563eb",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        Load
      </button>
      <div id="superset-container" style={{ marginTop: "1rem" }}></div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));  
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
